import {
  FormValues,
  UpgradePlanModal,
  UpgradePlanModalVariant,
} from '@/shared/components/UpgradePlanModal'
import { SubscriptionPlanType } from '@/types/subscription'
import {
  createContext,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useUserContext } from '../contexts/userContext'
import { useCreateSubscription } from '../hooks/data-hooks/subscriptions/useCreateSubscription'
import { useToast } from '../hooks/useToast'

export type UpgradePlanModalContextValue = {
  show: (variant: UpgradePlanModalVariant) => void
  hide: () => void
  isOpen: boolean
}

export const UpgradePlanModalContext = createContext<
  UpgradePlanModalContextValue | undefined
>(undefined)

type UpgradePlanModalProviderProps = {
  children: ReactNode
}

export function UpgradePlanModalProvider({
  children,
}: UpgradePlanModalProviderProps) {
  const { user } = useUserContext()
  const { toast } = useToast()
  const [isOpen, setIsOpen] = useState(false)
  const { mutateAsync: createSubscription } = useCreateSubscription()

  const [variant, setVariant] =
    useState<UpgradePlanModalVariant>('limited_feature')

  const show = useCallback((modalVariant: UpgradePlanModalVariant) => {
    setVariant(modalVariant)
    setIsOpen(true)
  }, [])

  const hide = useCallback(() => {
    setIsOpen(false)
  }, [])

  const handlePlanChange = useCallback(
    async (subscriptionPlanType: SubscriptionPlanType) => {
      try {
        if (!user) {
          throw new Error('User not found')
        }

        const { data } = await createSubscription({
          subscriptionPlanType,
        })

        if (data.checkoutUrl) {
          window.location.href = data.checkoutUrl
        }
      } catch (error) {
        toast({
          variant: 'destructive',
          title: 'Failed to update plan',
          description:
            error.message || 'An unexpected error occurred. Please try again.',
        })
      }
    },
    [createSubscription, toast, user]
  )

  const handleSubmit = useCallback(
    async (data?: FormValues) => {
      if (data?.planType) {
        return await handlePlanChange(data.planType)
      }

      hide()
    },
    [hide, handlePlanChange]
  )

  const contextValue = useMemo(
    () => ({ show, hide, isOpen }),
    [show, hide, isOpen]
  )

  return (
    <UpgradePlanModalContext.Provider value={contextValue}>
      {children}
      <UpgradePlanModal
        open={isOpen}
        onSubmit={handleSubmit}
        variant={variant}
      />
    </UpgradePlanModalContext.Provider>
  )
}
